import { useEffect, useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { intlLocaleOf } from "@/lib/i18n/intlLocale";
import {
  CAPABILITY_CATALOGUE,
  actionBundleForColumns,
  missingActionCaps,
  type Capability,
} from "@/types/Capability";
import { useQuery } from "@tanstack/react-query";
import { IconSearch, IconX, IconPlus, IconRefresh } from "@tabler/icons-react";
import type { Filter } from "@/types/Filter";
import type { Field } from "@/types/Field";
import type { TableFilter } from "@/types/TableFilter";
import type { TableView } from "@/types/TableView";
import type { ServerError } from "@/lib/api/errors/ErrorHandler";
import {
  fetchFiltersList,
  fetchViewsList,
} from "@/lib/api/handlers/ViewFilterHandling";
import {
  FILTERS_FIRST_PAGE,
  VIEWS_FIRST_PAGE,
} from "@/lib/template-transfer/tableViews/constants";
import { ModalWrapper } from "@/components/ModalWrapper/ModalWrapper";
import { Card } from "@/components/Card/Card";
import { FormInput } from "@/components/Inputs/FormInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { InlineLoadingSpinner } from "@/components/LoadingSpinner/InlineLoadingSpinner";
import {
  categorizeViewFields,
  countFilterImport,
  countViewImport,
} from "./roleTemplateImport";
import { FilterConditionsPreview } from "./FilterConditionsPreview";
import { ViewFieldsPreview } from "./ViewFieldsPreview";

type Entry = TableFilter | TableView;
const entryId = (kind: "filter" | "view", e: Entry) =>
  kind === "filter"
    ? (e as TableFilter).tableFilterId
    : (e as TableView).tableViewId;

/**
 * Master-detail modal to import a saved guest-list filter / view into a role
 * template. Left: searchable list. Right: live preview of the highlighted
 * entry's real conditions / columns. Footer: a truthful summary + explicit
 * Add (append, deduped) vs Replace (overwrite). Opens above the editor (z-1000).
 */
export function ImportFromGuestListModal({
  kind,
  eventId,
  allFields,
  fieldLabelMap,
  currentRowFilters,
  currentAllowlist,
  currentCapabilities,
  onApply,
  onClose,
}: {
  kind: "filter" | "view";
  eventId: string;
  allFields: Field[];
  fieldLabelMap: Record<string, string>;
  currentRowFilters: Filter[];
  currentAllowlist: string[] | null;
  /**
   * The role's ticked capabilities. A saved view's "Aktion" column follows a
   * capability, so importing the view turns that capability (and the field its
   * action reads) on — the footer says so BEFORE the click, naming only what
   * the role does not carry yet. Absent ⇒ the footer stays silent about it.
   */
  currentCapabilities?: ReadonlySet<Capability>;
  /** mode + payload (filter conditions or view field names) for the editor. */
  onApply: (mode: "add" | "replace", payload: Filter[] | string[]) => void;
  onClose: () => void;
}) {
  const t = useTranslations("roleTemplates");
  const tRoot = useTranslations();
  const tCommon = useTranslations("eventSettings");
  const tFields = useTranslations("guest-fields");
  const locale = useLocale();

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const query = useQuery<Entry[] | ServerError>({
    queryKey: ["roleTemplateGuestList", kind, eventId],
    // The two lists sit on different pager bases: filters ZERO-based, views
    // ONE-based. The literal 0 that stood on the views line was a 400 the
    // handler swallowed into "no saved views" (PB-RTI-1).
    queryFn: () =>
      kind === "filter"
        ? fetchFiltersList([], "guest", eventId, FILTERS_FIRST_PAGE, 100)
        : fetchViewsList([], "guest", eventId, VIEWS_FIRST_PAGE, 100),
    enabled: !!eventId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const rows = useMemo(
    () => (Array.isArray(query.data) ? query.data : []),
    [query.data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description?.toLowerCase().includes(q) ?? false),
    );
  }, [rows, search]);

  // Keep a valid selection: default to the first visible entry.
  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedId(undefined);
      return;
    }
    if (!filtered.some((r) => entryId(kind, r) === selectedId)) {
      setSelectedId(entryId(kind, filtered[0]));
    }
  }, [filtered, kind, selectedId]);

  // Escape closes (ModalWrapper only handles backdrop click).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const selected = filtered.find((r) => entryId(kind, r) === selectedId);

  const moveSelection = (delta: number) => {
    if (filtered.length === 0) return;
    const i = filtered.findIndex((r) => entryId(kind, r) === selectedId);
    const next = Math.max(0, Math.min(filtered.length - 1, (i < 0 ? 0 : i) + delta));
    setSelectedId(entryId(kind, filtered[next]));
  };

  const labelFor = (n: string) => fieldLabelMap[n] ?? tFields(n);
  // Label for a RESOLVED field def — FilterConditionsPreview resolves keys
  // itself (both shapes) and asks only for the display text, so it never needs a
  // key-shaped label map.
  const labelForField = (f: Field) =>
    f.additionalField ? f.translation || f.name : tFields(f.name);

  const apply = (mode: "add" | "replace") => {
    if (!selected) return;
    const payload =
      kind === "filter"
        ? (selected as TableFilter).filter ?? []
        : (selected as TableView).fields ?? [];
    onApply(mode, payload);
  };

  // Footer summary for the highlighted entry.
  const summary = (() => {
    if (!selected) return null;
    if (kind === "filter") {
      const { total, fresh, existing } = countFilterImport(
        currentRowFilters,
        (selected as TableFilter).filter ?? [],
        // Same key space as the merge, or the footer promises rows the merge then
        // dedupes away: `currentRowFilters` is canonical, a saved guest-list
        // filter carries bare names.
        allFields,
      );
      return t("filterSummary", { total, fresh, existing });
    }
    const { kept, fresh, droppedLabels } = countViewImport(
      currentAllowlist,
      (selected as TableView).fields ?? [],
      allFields,
    );
    const main = t("viewSummary", { kept, fresh });
    const parts = [main];
    if (droppedLabels.length > 0) {
      parts.push(
        t("columnsSkipped", { count: droppedLabels.length, names: droppedLabels.join(", ") }),
      );
    }
    // The view's "Aktion" column(s): what the import turns on beyond fields —
    // only the parts the role lacks, so a role that already has them reads
    // nothing here.
    if (currentCapabilities) {
      const viewFields = (selected as TableView).fields ?? [];
      const bundle = actionBundleForColumns(
        categorizeViewFields(viewFields, allFields).implicit,
      );
      const granted = new Set(currentAllowlist ?? []);
      const items = [
        ...missingActionCaps(bundle, new Set(currentCapabilities)).map((cap) =>
          tRoot(CAPABILITY_CATALOGUE.find((d) => d.key === cap)?.i18nKey ?? cap, {
            defaultValue: cap,
          }),
        ),
        // Unrestricted (`null`) sees every field — nothing to name.
        ...(currentAllowlist === null
          ? []
          : bundle.fields.filter((f) => !granted.has(f)).map((f) => tFields(f))),
      ];
      if (items.length > 0) {
        parts.push(
          t("viewImportActivates", {
            items: new Intl.ListFormat(intlLocaleOf(locale), {
              style: "long",
              type: "conjunction",
            }).format(items),
          }),
        );
      }
    }
    return parts.join(" · ");
  })();

  const hasSelection = !!selected;

  return (
    <ModalWrapper onClose={onClose} zIndex="high" size="max-w-3xl">
      <Card
        padding="p-0"
        width="w-[95vw] sm:max-w-3xl"
        height="max-h-[85dvh]"
        justify=""
        flex="flex flex-col"
        className="overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-3 shrink-0">
          <h2 className="h2 truncate min-w-0">
            {kind === "filter" ? t("importTitleFilter") : t("importTitleView")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={tCommon("cancel")}
            className="p-1.5 rounded-lg hover:surface-tertiary transition-colors cursor-pointer shrink-0"
          >
            <IconX size={18} className="font-secondary" />
          </button>
        </div>

        {/* Body: master | detail */}
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-0 flex-1 min-h-0 border-t outline-quaternary">
          {/* Master list */}
          <div
            className="flex flex-col min-h-0 border-b sm:border-b-0 sm:border-r outline-quaternary"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                moveSelection(1);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                moveSelection(-1);
              }
            }}
          >
            <div className="p-3 shrink-0">
              <FormInput
                id="import-search"
                search
                placeholder={t("search")}
                value={search}
                onChangeAction={setSearch}
                icon={IconSearch}
                deletion
                margin=""
                onEnterAction={() => apply("add")}
              />
            </div>
            <ul className="flex flex-col gap-0.5 overflow-y-auto px-2 pb-3 min-h-[180px] max-h-[55dvh]">
              {query.isLoading || (query.isFetching && rows.length === 0) ? (
                <li className="py-6">
                  <InlineLoadingSpinner />
                </li>
              ) : filtered.length === 0 ? (
                <li className="px-2 py-6 body-3-regular font-tertiary text-center">
                  {kind === "filter" ? t("noSavedFilters") : t("noSavedViews")}
                </li>
              ) : (
                filtered.map((r) => {
                  const id = entryId(kind, r);
                  const active = id === selectedId;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(id)}
                        aria-current={active}
                        className={`w-full text-left px-2.5 py-2 rounded-lg cursor-pointer flex flex-col gap-0.5 transition-colors ${
                          active ? "surface-secondary" : "hover:surface-tertiary"
                        }`}
                      >
                        <span className="body-3-medium font-secondary truncate">
                          {r.name}
                        </span>
                        {r.description && (
                          <span className="body-4-regular font-tertiary truncate">
                            {r.description}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>

          {/* Detail preview */}
          <div className="flex flex-col min-h-0 p-4 overflow-y-auto">
            <p className="body-4-bold font-tertiary uppercase tracking-wide mb-2 shrink-0">
              {t("preview")}
            </p>
            {!hasSelection ? (
              <p className="body-3-regular font-tertiary py-6">
                {t("previewEmpty")}
              </p>
            ) : kind === "filter" ? (
              <FilterConditionsPreview
                filters={(selected as TableFilter).filter ?? []}
                allFields={allFields}
                labelFor={labelForField}
              />
            ) : (
              <ViewFieldsPreview
                fields={(selected as TableView).fields ?? []}
                allFields={allFields}
                labelFor={labelFor}
                skippedTitle={t("skippedTitle")}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 shrink-0 border-t outline-quaternary">
          <p className="body-3-regular font-tertiary min-w-0">{summary}</p>
          <div className="flex items-center gap-2 shrink-0">
            <SecondaryButton
              text={tCommon("cancel")}
              size="small"
              width="w-max"
              onClick={onClose}
            />
            <SecondaryButton
              text={t("replace")}
              size="small"
              width="w-max"
              icon={IconRefresh}
              disabled={!hasSelection}
              onClick={() => apply("replace")}
            />
            <PrimaryButton
              text={t("add")}
              size="small"
              width="w-max"
              icon={IconPlus}
              disabled={!hasSelection}
              onClick={() => apply("add")}
            />
          </div>
        </div>
      </Card>
    </ModalWrapper>
  );
}
